<template>
  <div>
    <div class="evac-banner" v-if="evacuation">
      <font-awesome-icon icon="exclamation-triangle" class="evac-banner-icon" />
      <div class="evac-banner-text">
        <strong>EVACUATION IN PROGRESS</strong>
        <span>Started {{ formatDt(evacuation.started_at) }}</span>
        <span v-if="evacuation.note">{{ evacuation.note }}</span>
      </div>
      <button class="btn-end-evac" @click="confirmEnd = true">End Evacuation</button>
    </div>

    <div class="page-header">
      <div>
        <h5 class="page-title">Evacuation Mode</h5>
        <p class="page-sub">
          <span v-if="evacuation">Mark visitors as safe as they reach the muster point.</span>
          <span v-else>No active evacuation — start one from Roll-Call or click below.</span>
        </p>
      </div>
      <div style="display:flex;gap:8px;">
        <button class="btn-ghost" @click="load"><font-awesome-icon icon="sync-alt" :spin="loading" /> Refresh</button>
        <button v-if="!evacuation" class="btn-danger" @click="startPrompt = true">
          <font-awesome-icon icon="exclamation-triangle" /> Start Evacuation
        </button>
      </div>
    </div>

    <div v-if="loading" class="sk-table"><div v-for="n in 5" :key="n" class="sk-row"></div></div>

    <div v-else-if="!evacuation" class="empty-state">
      <font-awesome-icon icon="shield-alt" class="empty-icon empty-icon--green" />
      <p class="empty-title">No active evacuation</p>
      <p class="empty-sub">When an evacuation is started, this page shows all visitors currently on site. Mark each one safe as they reach the muster point.</p>
    </div>

    <div v-else>
      <!-- Stats -->
      <div class="evac-stats">
        <div class="stat-card stat-card--total">
          <div class="stat-num">{{ total }}</div>
          <div class="stat-label">Total on site</div>
        </div>
        <div class="stat-card stat-card--safe">
          <div class="stat-num">{{ safeCount }}</div>
          <div class="stat-label">Accounted for</div>
        </div>
        <div class="stat-card stat-card--missing">
          <div class="stat-num">{{ total - safeCount }}</div>
          <div class="stat-label">Unaccounted</div>
        </div>
        <div class="stat-card stat-card--progress">
          <div class="stat-progress-bar">
            <div class="stat-progress-fill" :style="{ width: total ? `${(safeCount / total * 100)}%` : '0%' }"></div>
          </div>
          <div class="stat-label">{{ total ? Math.round(safeCount / total * 100) : 0 }}% accounted for</div>
        </div>
      </div>

      <!-- Muster point filter -->
      <div class="filter-bar">
        <input v-model="muSearch" class="filter-input" placeholder="Search visitor name…" />
        <select v-model="filterSafe" class="filter-select">
          <option value="">All visitors</option>
          <option value="safe">Safe only</option>
          <option value="missing">Unaccounted only</option>
        </select>
      </div>

      <!-- Visitor list -->
      <div class="evac-list">
        <div v-for="v in filteredVisitors" :key="v.id" class="evac-row" :class="{ 'evac-row--safe': v.safe_at }">
          <div class="evac-visitor-info">
            <div class="evac-av" :class="v.safe_at ? 'evac-av--safe' : 'evac-av--missing'">
              {{ v.visitor_name?.slice(0,2).toUpperCase() ?? '?' }}
            </div>
            <div>
              <div class="evac-name">{{ v.visitor_name }}</div>
              <div class="evac-meta">{{ v.visitor_company ?? '' }}{{ v.visitor_company && v.host_name ? ' · ' : '' }}Host: {{ v.host_name ?? '—' }}</div>
            </div>
          </div>
          <div class="evac-badge-wrap">
            <span class="badge-chip">{{ v.badge_no ?? '—' }}</span>
          </div>
          <div v-if="v.safe_at" class="evac-safe-info">
            <font-awesome-icon icon="check-circle" style="color:#16a34a;" />
            <span>Safe {{ v.muster_point ? `— ${v.muster_point}` : '' }}</span>
            <span class="safe-time">{{ formatTime(v.safe_at) }}</span>
          </div>
          <div v-else class="evac-mark-safe">
            <input v-model="musterPoints[v.id]" class="muster-input" placeholder="Muster point" />
            <button class="btn-mark-safe" @click="markSafe(v)" :disabled="marking === v.id">
              <font-awesome-icon v-if="marking === v.id" icon="spinner" spin />
              <font-awesome-icon v-else icon="check" />
              Mark Safe
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Start Evacuation Prompt -->
    <div v-if="startPrompt" class="modal-backdrop" @mousedown.self="startPrompt = false">
      <div class="modal-box modal-box--sm">
        <div class="modal-header evac-header">
          <h6 class="modal-title evac-title"><font-awesome-icon icon="exclamation-triangle" /> Start Evacuation</h6>
          <button class="modal-close" @click="startPrompt = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <p style="font-size:13px;color:#4a5e78;margin:0 0 16px;">Initiate an evacuation event for this site. All receptionists and security will see the live roll-call.</p>
          <div class="form-row">
            <label class="form-label">Note (optional)</label>
            <input v-model="startNote" class="form-input" placeholder="e.g. Fire alarm — muster at car park" />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="startPrompt = false">Cancel</button>
          <button class="btn-danger" @click="doStart" :disabled="starting">
            <font-awesome-icon v-if="starting" icon="spinner" spin />
            {{ starting ? 'Starting…' : 'Confirm Evacuation' }}
          </button>
        </div>
      </div>
    </div>

    <!-- End Evacuation Confirm -->
    <div v-if="confirmEnd" class="modal-backdrop" @mousedown.self="confirmEnd = false">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <h6 class="modal-title">End Evacuation?</h6>
          <button class="modal-close" @click="confirmEnd = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <p style="font-size:13px;color:#4a5e78;">
            <strong>{{ total - safeCount }}</strong> visitors are still unaccounted for. Confirm that all personnel are safe before ending.
          </p>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="confirmEnd = false">Cancel</button>
          <button class="btn-approve" @click="doEnd" :disabled="ending">
            <font-awesome-icon v-if="ending" icon="spinner" spin />
            {{ ending ? 'Ending…' : 'End Evacuation' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { useToast } from 'vue-toastification'

export default {
  name: 'VisitorEvacuation',
  setup() { return { toast: useToast() } },
  data() {
    return {
      loading: true,
      evacuation: null,
      visitors: [],
      muSearch: '',
      filterSafe: '',
      musterPoints: {},
      marking: null,
      startPrompt: false, startNote: '', starting: false,
      confirmEnd: false, ending: false,
    }
  },
  computed: {
    total() { return this.visitors.length },
    safeCount() { return this.visitors.filter(v => v.safe_at).length },
    filteredVisitors() {
      let v = this.visitors
      if (this.muSearch) { const s = this.muSearch.toLowerCase(); v = v.filter(x => x.visitor_name?.toLowerCase().includes(s)) }
      if (this.filterSafe === 'safe')    v = v.filter(x => x.safe_at)
      if (this.filterSafe === 'missing') v = v.filter(x => !x.safe_at)
      return v
    },
  },
  created() { this.load(); this._timer = setInterval(() => this.load(false), 20000) },
  beforeUnmount() { clearInterval(this._timer) },
  methods: {
    async load(showLoading = true) {
      if (showLoading) this.loading = true
      try {
        const r = await axios.get('visitors/evacuation/current')
        const d = r.data.data ?? r.data
        this.evacuation = d.evacuation ?? null
        this.visitors   = d.visitors  ?? []
      } catch { this.toast.error('Failed to load evacuation data.') }
      finally { this.loading = false }
    },
    async markSafe(v) {
      this.marking = v.id
      try {
        await axios.post(`visitors/evacuation/safe/${v.id}`, { muster_point: this.musterPoints[v.id] ?? null })
        const idx = this.visitors.findIndex(x => x.id === v.id)
        if (idx >= 0) {
          this.visitors[idx].safe_at = new Date().toISOString()
          this.visitors[idx].muster_point = this.musterPoints[v.id] ?? null
        }
        delete this.musterPoints[v.id]
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to mark safe.') }
      finally { this.marking = null }
    },
    async doStart() {
      this.starting = true
      try {
        await axios.post('visitors/evacuation/start', { note: this.startNote || null })
        this.toast.success('Evacuation started.')
        this.startPrompt = false; await this.load()
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed.') }
      finally { this.starting = false }
    },
    async doEnd() {
      this.ending = true
      try {
        await axios.post(`visitors/evacuation/end/${this.evacuation.id}`)
        this.toast.success('Evacuation ended.')
        this.confirmEnd = false; await this.load()
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed.') }
      finally { this.ending = false }
    },
    formatDt(d) { return d ? new Date(d).toLocaleString('en-GB', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : '—' },
    formatTime(d) { return d ? new Date(d).toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit' }) : '' },
  },
}
</script>

<style scoped>
.evac-banner { display:flex; align-items:center; gap:14px; background:#dc2626; color:#fff; border-radius:12px; padding:14px 20px; margin-bottom:20px; flex-wrap:wrap; }
.evac-banner-icon { font-size:24px; flex-shrink:0; }
.evac-banner-text { flex:1; display:flex; flex-direction:column; gap:2px; }
.evac-banner-text strong { font-size:14px; font-weight:800; letter-spacing:.05em; }
.evac-banner-text span { font-size:12px; opacity:.85; }
.btn-end-evac { background:#fff; color:#dc2626; font-size:13px; font-weight:700; padding:0 18px; height:36px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; flex-shrink:0; }

.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }
.btn-ghost   { display:inline-flex; align-items:center; gap:6px; height:38px; padding:0 16px; border:1px solid #dde5f0; border-radius:8px; background:#fff; font-size:13px; color:#3d5068; cursor:pointer; font-family:inherit; }
.btn-ghost:hover { background:#f4f7fb; }
.btn-danger  { display:inline-flex; align-items:center; gap:6px; background:#dc2626; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-danger:disabled { opacity:.6; cursor:not-allowed; }
.btn-approve { display:inline-flex; align-items:center; gap:6px; background:#16a34a; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-approve:disabled { opacity:.6; cursor:not-allowed; }

.evac-stats { display:grid; grid-template-columns:repeat(auto-fill,minmax(140px,1fr)); gap:12px; margin-bottom:18px; }
.stat-card  { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:14px 16px; }
.stat-num   { font-size:28px; font-weight:800; }
.stat-label { font-size:12px; color:#7a90b0; font-weight:500; margin-top:3px; }
.stat-card--total   .stat-num { color:#1a2b45; }
.stat-card--safe    .stat-num { color:#16a34a; }
.stat-card--missing .stat-num { color:#dc2626; }
.stat-card--progress { display:flex; flex-direction:column; justify-content:center; gap:6px; }
.stat-progress-bar { height:8px; background:#f0f4f8; border-radius:4px; overflow:hidden; }
.stat-progress-fill { height:100%; background:#16a34a; border-radius:4px; transition:width .4s; }

.filter-bar { display:flex; gap:10px; flex-wrap:wrap; margin-bottom:14px; }
.filter-input  { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; min-width:200px; font-family:inherit; }
.filter-input:focus { border-color:#004AAD; }
.filter-select { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; background:#fff; cursor:pointer; font-family:inherit; }

.evac-list { display:flex; flex-direction:column; gap:8px; }
.evac-row  { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:12px 16px; display:flex; align-items:center; gap:12px; flex-wrap:wrap; }
.evac-row--safe { background:#f0fdf4; border-color:#86efac; }
.evac-visitor-info { display:flex; align-items:center; gap:10px; flex:1; min-width:180px; }
.evac-av   { width:36px; height:36px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:700; flex-shrink:0; }
.evac-av--safe    { background:#dcfce7; color:#16a34a; }
.evac-av--missing { background:#fee2e2; color:#dc2626; }
.evac-name { font-size:13px; font-weight:700; color:#1a2b45; }
.evac-meta { font-size:12px; color:#7a90b0; margin-top:1px; }
.evac-badge-wrap { flex-shrink:0; }
.badge-chip { background:#e8eef8; color:#004AAD; font-size:11px; font-weight:700; padding:3px 8px; border-radius:6px; font-family:monospace; }

.evac-safe-info { display:flex; align-items:center; gap:6px; font-size:13px; color:#16a34a; font-weight:600; }
.safe-time { font-size:11px; color:#7a90b0; font-weight:400; margin-left:4px; }
.evac-mark-safe { display:flex; align-items:center; gap:8px; }
.muster-input { padding:7px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:12px; color:#1a2b45; outline:none; width:140px; font-family:inherit; }
.muster-input:focus { border-color:#16a34a; }
.btn-mark-safe { display:inline-flex; align-items:center; gap:6px; background:#16a34a; color:#fff; font-size:12px; font-weight:600; padding:0 14px; height:34px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; white-space:nowrap; }
.btn-mark-safe:disabled { opacity:.6; cursor:not-allowed; }

.empty-state { text-align:center; padding:60px 20px; }
.empty-icon  { font-size:48px; color:#dde5f0; margin-bottom:16px; }
.empty-icon--green { color:#86efac; }
.empty-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 8px; }
.empty-sub   { font-size:13px; color:#7a90b0; margin:0; max-width:440px; margin-left:auto; margin-right:auto; }

.modal-backdrop { position:fixed; inset:0; background:rgba(10,20,40,.45); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
.modal-box { background:#fff; border-radius:16px; width:100%; max-width:420px; max-height:90vh; overflow-y:auto; display:flex; flex-direction:column; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-box--sm { max-width:420px; }
.modal-header { display:flex; align-items:center; justify-content:space-between; padding:18px 22px 14px; border-bottom:1px solid #f0f4f8; }
.evac-header  { background:#fff5f5; }
.modal-title  { font-size:16px; font-weight:700; color:#1a2b45; margin:0; }
.evac-title   { color:#dc2626; }
.modal-close  { background:none; border:none; font-size:16px; color:#7a90b0; cursor:pointer; padding:4px; }
.modal-body   { padding:22px; }
.modal-footer { padding:14px 22px; border-top:1px solid #f0f4f8; display:flex; justify-content:flex-end; gap:10px; }
.form-row  { display:flex; flex-direction:column; gap:5px; }
.form-label { font-size:12px; font-weight:600; color:#4a5e78; }
.form-input { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; font-family:inherit; }

.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.sk-row   { height:52px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-bottom:1px solid #e4eaf2; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
</style>
